import { PRODUCT_SLUGS } from '@discover-frontend/constants';

export const PRODUCT_SCOPES = {
  US: 'us',
  US_GWI: 'us-gwi',
  SM_US_MRI: 'sm-us-mri',
  SM_US_GWI: 'sm-us-gwi',
  SM_WW_GWI: 'sm-ww-gwi',
  SM_GB_TGI: 'sm-gb-tgi',
  WW_GWI_MED: 'ww-gwi-med',
  WORLD_WIDE: 'world-wide',
} as const;

type ProductScopeType = (typeof PRODUCT_SCOPES)[keyof typeof PRODUCT_SCOPES];

export const getProductScope = (): ProductScopeType => {
  const hostname = window.location.hostname;

  if (hostname) {
    if (hostname.includes('discovery-us-gwi')) {
      return PRODUCT_SCOPES.US_GWI;
    } else if (hostname.includes('discovery-us')) {
      return PRODUCT_SCOPES.US;
    } else if (hostname.includes('discover-us-gwi')) {
      return PRODUCT_SCOPES.SM_US_GWI;
    } else if (hostname.includes('discover-us')) {
      return PRODUCT_SCOPES.SM_US_MRI;
    } else if (hostname.includes('discover-gb')) {
      return PRODUCT_SCOPES.SM_GB_TGI;
    } else if (hostname.includes('discovery-global')) {
      return PRODUCT_SCOPES.WW_GWI_MED;
    } else if (hostname.includes('discover-global')) {
      return PRODUCT_SCOPES.SM_WW_GWI;
    } else if (hostname.includes('discovery-ww')) {
      return PRODUCT_SCOPES.WORLD_WIDE;
    }
  }

  return PRODUCT_SCOPES.WORLD_WIDE;
};

const isScope = (scope: ProductScopeType) => (): boolean =>
  getProductScope() === scope;

export const isUs = isScope(PRODUCT_SCOPES.US);

export const isUsGwi = isScope(PRODUCT_SCOPES.US_GWI);

export const isSmUsMri = isScope(PRODUCT_SCOPES.SM_US_MRI);

export const isSmUsGwi = isScope(PRODUCT_SCOPES.SM_US_GWI);

export const isSmWwGwi = isScope(PRODUCT_SCOPES.SM_WW_GWI);

export const isSmGbTgi = isScope(PRODUCT_SCOPES.SM_GB_TGI);

export const isWwGwiMed = isScope(PRODUCT_SCOPES.WW_GWI_MED);

export const isWW = isScope(PRODUCT_SCOPES.WORLD_WIDE);

export const isGenericUs = (): boolean =>
  isUs() || isSmUsMri() || isSmUsGwi() || isUsGwi();

export const isGenericGb = (): boolean => isSmGbTgi();

export const hasDiscoverTaxonomy = (): boolean =>
  isUs() ||
  isSmUsMri() ||
  isSmUsGwi() ||
  isUsGwi() ||
  isWwGwiMed() ||
  isSmWwGwi();

/** Products that keep Psychographics → Lifestyles/Personalities hierarchy (not WW legacy flattening). */
export const keepsNativePsychographicTree = (): boolean =>
  hasDiscoverTaxonomy() || isGenericGb();

export const getExtendedProductScopeUsOrGlobal = (): string => {
  const productScope = getProductScope();

  if (productScope === PRODUCT_SCOPES.WORLD_WIDE) return `affinity-global`;

  return `affinity-${productScope}`;
};

export const SCOPE_LABELS = {
  US_POPULATION: 'US Population',
  US_POPULATION_21_PLUS: 'US+ Population 21+',
  US_PLUS_POPULATION: 'US+ Population',
  US_PLUS_GWI_POPULATION: 'US+ GWI Population',
  WW_PLUS_GWI_POPULATION: 'Global+ Population',
  GB_PLUS_POPULATION: 'GB+ Population',
  GLOBAL_FUSION_POPULATION: 'Global Fusion population',
  GLOBAL_POPULATION: 'Global Population',
};

const POPULATION_LABELS = {
  [PRODUCT_SCOPES.US]: SCOPE_LABELS.US_POPULATION,
  [PRODUCT_SCOPES.US_GWI]: SCOPE_LABELS.US_POPULATION,
  [PRODUCT_SCOPES.SM_US_MRI]: SCOPE_LABELS.US_PLUS_POPULATION,
  [PRODUCT_SCOPES.SM_US_GWI]: SCOPE_LABELS.US_PLUS_GWI_POPULATION,
  [PRODUCT_SCOPES.SM_GB_TGI]: SCOPE_LABELS.GB_PLUS_POPULATION,
  [PRODUCT_SCOPES.SM_WW_GWI]: SCOPE_LABELS.WW_PLUS_GWI_POPULATION,
  [PRODUCT_SCOPES.WW_GWI_MED]: SCOPE_LABELS.GLOBAL_FUSION_POPULATION,
  [PRODUCT_SCOPES.WORLD_WIDE]: SCOPE_LABELS.GLOBAL_POPULATION,
};

export const getPopulationLabel = (): string =>
  POPULATION_LABELS[getProductScope()];

export const PRODUCT_SCOPE_LABELS = {
  [PRODUCT_SCOPES.US]: 'US',
  [PRODUCT_SCOPES.US_GWI]: 'US GWI',
  [PRODUCT_SCOPES.SM_US_MRI]: 'US+',
  [PRODUCT_SCOPES.SM_US_GWI]: 'US+ GWI',
  [PRODUCT_SCOPES.SM_WW_GWI]: 'Global+',
  [PRODUCT_SCOPES.SM_GB_TGI]: 'GB+',
  [PRODUCT_SCOPES.WW_GWI_MED]: 'Global',
  [PRODUCT_SCOPES.WORLD_WIDE]: 'WW',
};

export const DISCOVER_TITLE_LABELS = Object.fromEntries(
  Object.entries(PRODUCT_SCOPE_LABELS).map(([key, value]) => [
    key,
    `Discover ${value}`,
  ])
);

export const getDocumentTitleByProductScope = (): string =>
  DISCOVER_TITLE_LABELS[getProductScope()];

const PRODUCT_SLUG_BY_PRODUCT_SCOPE = {
  [PRODUCT_SCOPES.US_GWI]: PRODUCT_SLUGS.AFFINITY_US_GWI,
  [PRODUCT_SCOPES.WW_GWI_MED]: PRODUCT_SLUGS.AFFINITY_WW_GWI_MED,
  [PRODUCT_SCOPES.US]: PRODUCT_SLUGS.AFFINITY_US,
  [PRODUCT_SCOPES.WORLD_WIDE]: PRODUCT_SLUGS.AFFINITY_GLOBAL,
  [PRODUCT_SCOPES.SM_US_MRI]: PRODUCT_SLUGS.AFFINITY_SM_US_MRI,
  [PRODUCT_SCOPES.SM_US_GWI]: PRODUCT_SLUGS.AFFINITY_SM_US_GWI,
  [PRODUCT_SCOPES.SM_WW_GWI]: PRODUCT_SLUGS.AFFINITY_SM_WW_GWI,
  [PRODUCT_SCOPES.SM_GB_TGI]: PRODUCT_SLUGS.AFFINITY_SM_GB_TGI,
};

export const getProductSlug = (): string =>
  PRODUCT_SLUG_BY_PRODUCT_SCOPE[getProductScope()];
